/**
 * STYRKR BYOB Prefill Bridge v4
 * -------------------------------------------------------
 * Confirmed Recharge DOM (live page):
 *   Root:          #build-a-box
 *   Cards:         #build-a-box .rb-items-wrapper .tflex-column (3 per product, only first has buttons)
 *   Pack size:     select.rb-variant-selector inside each card  (NOT radio buttons)
 *   Subscribe:     input[type="checkbox"] inside .rb-subscribe-and-save-container (ONE global toggle)
 *   + button:      last <button> in card, or span.tw1 containing "+"
 *
 * Fixes vs v3:
 *   - `const params` renamed to `const _qs` to avoid conflict with other page scripts
 *   - selectPackSize now uses <select> + change event (not radio/label click)
 *   - subscribe checkbox clicked after widget ready (before applying lines)
 */

(function () {

  // ── 1. Strip ?prefill from URL BEFORE Recharge reads query params ──────────
  // Renamed to _qs to avoid `params` conflict with Shoplift / other scripts
  const _qs = new URLSearchParams(window.location.search);
  const _token = _qs.get("prefill");

  if (_token) {
    _qs.delete("prefill");
    const _clean = location.pathname + (_qs.toString() ? "?" + _qs.toString() : "");
    history.replaceState(null, "", _clean);
  }

  if (!_token) return;

  // ── 2. Validation ──────────────────────────────────────────────────────────
  if (_token.length > 8192) {
    console.warn("[STYRKR Prefill] Token too large — ignoring.");
    return;
  }

  function base64UrlDecode(b64url) {
    const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((b64url.length + 3) % 4);
    return new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
  }

  let payload;
  try { payload = JSON.parse(base64UrlDecode(_token)); }
  catch (e) { console.warn("[STYRKR Prefill] Bad token", e); return; }

  if (!payload || payload.v !== 1 || !Array.isArray(payload.lines)) return;

  const MAX_QTY = 60;
  const lines = payload.lines
    .filter((l) => l && l.packKey && Number(l.qty) > 0)
    .map((l) => ({
      packKey: String(l.packKey).replace(/[^A-Z0-9_]/gi, "").toUpperCase(),
      qty: Math.min(Math.floor(Number(l.qty)), MAX_QTY),
    }))
    .filter((l) => l.packKey.length > 0 && l.qty > 0);

  if (!lines.length) return;

  // ── 3. packKey → card title fragment ──────────────────────────────────────
  const PACK_MAP = {
    MIXPLUS:    { title: "MIX+",                           sizes: [15, 25] },
    MIX60:      { title: "MIX60",                          sizes: [6, 12]  },
    MIX90_CAFF: { title: "MIX90 Caffeine",                 sizes: [6, 12]  },
    MIX90:      { title: "MIX90 Dual-Carb",                sizes: [6, 12]  },
    GEL30_CAFF: { title: "GEL30 Caffeine",                 sizes: [6, 12]  },
    GEL30:      { title: "GEL30 Dual-Carb",                sizes: [6, 12]  },
    GEL50:      { title: "GEL50",                          sizes: [6, 12]  },
    BAR30:      { title: "BAR30",                          sizes: [12]     },
    BAR50:      { title: "BAR50",                          sizes: [6, 12]  },
    SLT07_1000: { title: "1000MG",                         sizes: [36, 72] },
    SLT07_500:  { title: "500MG",                          sizes: [12, 36] },
    SLTPLUS:    { title: "High-Strength Electrolyte",      sizes: [30]     },
  };

  // Extract SKU prefix and pack size from packKey e.g. "GEL30_6" → { sku:"GEL30", size:6 }
  function parsePackKey(packKey) {
    const m = packKey.match(/^([A-Z0-9]+(?:_[A-Z]+)*)_(\d+)$/);
    if (!m) return { sku: packKey, size: null };
    return { sku: m[1], size: Number(m[2]) };
  }

  // ── 4. Card helpers ────────────────────────────────────────────────────────
  function getAllCards() {
    return Array.from(document.querySelectorAll(
      "#build-a-box .rb-items-wrapper .tflex-column"
    )).filter((c) => c.querySelector("button"));
  }

  function findCard(sku) {
    const meta = PACK_MAP[sku];
    if (!meta) return null;
    return getAllCards().find((c) =>
      (c.textContent || "").toLowerCase().includes(meta.title.toLowerCase())
    ) || null;
  }

  // ── 5. Pack size selection via <select> ────────────────────────────────────
  // Recharge uses: <select class="rb-variant-selector ...">
  //                  <option>6 pack</option>
  //                  <option>12 pack</option>
  //                </select>
  function selectPackSize(card, size) {
    if (!size) return;

    const sel = card.querySelector("select.rb-variant-selector") ||
                card.querySelector("select[class*='rb-']") ||
                card.querySelector("select");

    if (!sel) {
      console.warn("[STYRKR Prefill] No <select> found in card for size", size);
      return;
    }

    // Find the option whose text includes our size number
    const target = Array.from(sel.options).find((o) =>
      o.text.toLowerCase().includes(String(size))
    );

    if (!target) {
      console.warn("[STYRKR Prefill] No option found for size", size, "in", sel.id || sel.className);
      return;
    }

    sel.value = target.value;
    sel.dispatchEvent(new Event("change", { bubbles: true }));
    sel.dispatchEvent(new Event("input",  { bubbles: true }));
    console.info("[STYRKR Prefill] Pack size →", target.text);
  }

  // ── 6. Subscribe toggle ────────────────────────────────────────────────────
  // payload.subscribe === true  → tick checkbox (subscribe & save)
  // payload.subscribe === false → untick checkbox (one-time)
  // payload.subscribe absent    → don't touch it, leave Recharge's default
  function setSubscribe(wantSubscribe) {
    if (wantSubscribe === undefined || wantSubscribe === null) return; // absent — leave alone

    const container = document.querySelector(".rb-subscribe-and-save-container");
    if (!container) { console.warn("[STYRKR Prefill] Subscribe container not found."); return; }

    const checkbox = container.querySelector('input[type="checkbox"]');
    if (!checkbox) { console.warn("[STYRKR Prefill] Subscribe checkbox not found."); return; }

    if (wantSubscribe && !checkbox.checked) {
      checkbox.click();
      console.info("[STYRKR Prefill] ✓ Subscribe enabled.");
    } else if (!wantSubscribe && checkbox.checked) {
      checkbox.click();
      console.info("[STYRKR Prefill] ✓ One-time selected (subscribe off).");
    } else {
      console.info("[STYRKR Prefill] Subscribe state already correct.");
    }
  }

  // ── 7. Click + button N times ──────────────────────────────────────────────
  function clickPlus(card, n) {
    if (!card || n <= 0) return false;

    const btns = Array.from(card.querySelectorAll("button"));
    const plus = btns.find((b) =>
      /plus|add|increase|\+/i.test(b.getAttribute("aria-label") || b.textContent || "")
    ) || btns[btns.length - 1];

    if (!plus) {
      console.warn("[STYRKR Prefill] No + button found in card:", card.textContent?.trim().slice(0, 50));
      return false;
    }

    let i = 0;
    const iv = setInterval(() => {
      plus.click();
      if (++i >= n) clearInterval(iv);
    }, 100);

    return true;
  }

  // ── 8. Apply all lines ─────────────────────────────────────────────────────
  function applyLines() {
  let applied = 0;
  for (const line of lines) {
    const { sku, size } = parsePackKey(line.packKey);
    const card = findCard(sku);
    if (!card) {
      console.warn("[STYRKR Prefill] No card matched for:", line.packKey, "(sku:", sku + ")");
      continue;
    }

    // Reset qty to 0 first by clicking minus until 0
    const btns = Array.from(card.querySelectorAll("button"));
    const minus = btns.find((b) =>
      /minus|remove|decrease|-/i.test(b.getAttribute("aria-label") || b.textContent || "")
    ) || btns[0];
    if (minus) {
      for (let i = 0; i < 20; i++) minus.click(); // hammer it to 0
    }

    if (size) selectPackSize(card, size);
    const ok = clickPlus(card, line.qty);
    if (ok) {
      applied++;
      console.info("[STYRKR Prefill] ✓", line.packKey, "→", line.qty, "×", size || "?");
    }
  }
  console.info(`[STYRKR Prefill] Done — triggered ${applied}/${lines.length} lines`);
  if (applied === 0) _showWarning("We couldn't auto-fill your selections — please choose products manually.");
}
  // Poll until subscribe container is in DOM, set it, then apply lines
  function applyPrefill() {
    console.info("[STYRKR Prefill] Applying", lines.length, "lines...");
    const wantSubscribe = typeof payload.subscribe === "boolean" ? payload.subscribe : null;

    if (wantSubscribe === null) {
      // No preference in payload — skip subscribe and go straight to lines
      applyLines();
      return;
    }

    // Subscribe container is mounted by Vue separately — poll for it
    const maxWait = 8000;
    const subStart = Date.now();
    const subTimer = setInterval(() => {
      const cb = document.querySelector(".rb-subscribe-and-save-container input[type='checkbox']");
      if (cb) {
        clearInterval(subTimer);
        setSubscribe(wantSubscribe);
        // Wait 300ms for Vue to re-render subscription pricing before clicking +
        setTimeout(applyLines, 300);
      } else if (Date.now() - subStart > maxWait) {
        clearInterval(subTimer);
        console.warn("[STYRKR Prefill] Subscribe container never appeared — applying lines anyway.");
        applyLines();
      }
    }, 100);
  }

  // ── 9. Warning banner ──────────────────────────────────────────────────────
  function _showWarning(message) {
    if (document.getElementById("styrkr-prefill-warning")) return;
    const target = document.querySelector("#build-a-box") || document.body;
    const el = document.createElement("div");
    el.id = "styrkr-prefill-warning";
    el.setAttribute("role", "alert");
    el.style.cssText = "background:#fff3cd;color:#7d5a00;border:1px solid #ffc107;" +
      "border-radius:4px;padding:10px 16px;margin:12px auto;max-width:700px;" +
      "font-family:sans-serif;font-size:14px;line-height:1.5;position:relative;z-index:9999";
    el.textContent = "⚠️ " + message;
    target.insertAdjacentElement("afterbegin", el);
  }

  // ── 10. Wait for widget ────────────────────────────────────────────────────
  const maxMs = 20000;
  const start = Date.now();
  let _done = false;

  function _isReady() {
    return getAllCards().length > 0;
  }

  function _tryApply() {
    if (_done) return;
    if (_isReady()) {
      _done = true;
      clearInterval(_timer);
      _observer.disconnect();
      // Wait 500ms for Vue to finish full render before touching anything
      setTimeout(applyPrefill, 500);
    }
  }

  const _observer = new MutationObserver(_tryApply);
  _observer.observe(document.body, { childList: true, subtree: true });

  const _timer = setInterval(() => {
    if (_done || Date.now() - start > maxMs) {
      clearInterval(_timer);
      _observer.disconnect();
      if (!_done) {
        console.warn("[STYRKR Prefill] Timed out waiting for widget.");
        _showWarning("The bundle builder took too long to load. Please choose products manually.");
      }
    } else {
      _tryApply();
    }
  }, 300);

  _tryApply();

})();